import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { identity } from '@kofno/piper';
import {
  array,
  boolean,
  date,
  Decoder,
  field,
  maybe,
  number,
  oneOf,
  string,
  succeed,
} from 'jsonous';
import { readonlyArray } from '../../../../../../Decoders';
import { resourceDecoder } from '../../../../../../Resource/Decoders';
import { shareUrlResourceDecoder } from '../../../../Decoders';
import {
  FailedParticipant,
  GroupMember,
  GroupParticipant,
  LearningCollection,
  License,
  LicenseOption,
  NotFoundUserParticipant,
  Participant,
  Program,
  ProgramFamily,
  ProgramKind,
  ShareLearningCollectionResponse,
  SuccessfulParticipant,
  UnlicensedUserParticipant,
  UnverifiedGroupParticipant,
  UserParticipant,
  VerifiedUserParticipant,
} from './Types';

export const licenseDecoder: Decoder<License> = succeed({})
  .assign('id', field('id', number))
  .assign('licenseTypeName', field('license_type_name', string))
  .assign('quantityAvailable', field('quantity_available', number))
  .assign('startOn', field('start_on', date))
  .assign('endOn', field('end_on', date));

export const licenseOptionDecoder: Decoder<LicenseOption> = succeed({})
  .assign('id', field('id', number))
  .assign('licenseName', field('license_name', explicitMaybe(string)))
  .assign('licenseTypeName', field('license_type_name', string))
  .assign('quantityAvailable', field('quantity_available', number))
  .assign('startOn', field('start_on', date))
  .assign('endOn', field('end_on', date));

const programKindDecoder: Decoder<ProgramKind> = oneOf([
  stringLiteral<ProgramKind>('on-demand'),
  stringLiteral<ProgramKind>('scheduled'),
]);

const programFamilyDecoder: Decoder<ProgramFamily> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('offeringType', field('offering_type', string))
  .assign('schoolName', field('school_name', explicitMaybe(string)));

export const programDecoder: Decoder<Program> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('kind', field('kind', programKindDecoder))
  .assign('startOn', field('start_on', date))
  .assign('programFamily', field('program_family', programFamilyDecoder));

const verifiedUserParticipantDecoder: Decoder<VerifiedUserParticipant> = succeed({})
  .assign('kind', field('kind', stringLiteral('user')))
  .assign('id', field('id', number))
  .assign('email', field('email', string))
  .assign('name', field('name', string))
  .assign('status', field('status', stringLiteral('verified')))
  .assign('licenses', field('licenses', readonlyArray(licenseDecoder)));

const notFoundUserParticipantDecoder: Decoder<NotFoundUserParticipant> = succeed({})
  .assign('kind', field('kind', stringLiteral('user')))
  .assign('email', field('email', string))
  .assign('name', field('name', string))
  .assign('status', field('status', stringLiteral('not-found')));

const unlicensedUserParticipantDecoder: Decoder<UnlicensedUserParticipant> = succeed({})
  .assign('kind', field('kind', stringLiteral('user')))
  .assign('id', field('id', number))
  .assign('email', field('email', string))
  .assign('name', field('name', string))
  .assign('status', field('status', stringLiteral('unlicensed')))
  .assign('licenses', field('licenses', readonlyArray(licenseDecoder)));

const userParticipantDecoder: Decoder<UserParticipant> = oneOf<UserParticipant>([
  verifiedUserParticipantDecoder.map<UserParticipant>(identity),
  notFoundUserParticipantDecoder.map<UserParticipant>(identity),
  unlicensedUserParticipantDecoder.map<UserParticipant>(identity),
]);

export const groupMemberDecoder: Decoder<GroupMember> = succeed({}).assign(
  'id',
  field('id', number),
);

const verifiedGroupParticipantDecoder = succeed({})
  .assign('kind', field('kind', stringLiteral('group')))
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('members', field('members', readonlyArray(groupMemberDecoder)))
  .assign('status', field('status', stringLiteral('verified')))
  .map<GroupParticipant | UnverifiedGroupParticipant>(identity);

const unverifiedGroupParticipantDecoder = succeed({})
  .assign('kind', field('kind', stringLiteral('group')))
  .assign('name', field('name', string))
  .assign('status', field('status', stringLiteral('not-found')))
  .map<GroupParticipant | UnverifiedGroupParticipant>(identity);

const groupParticipantDecoder: Decoder<GroupParticipant | UnverifiedGroupParticipant> = oneOf<
  GroupParticipant | UnverifiedGroupParticipant
>([verifiedGroupParticipantDecoder, unverifiedGroupParticipantDecoder]);

const participantDecoder: Decoder<Participant> = oneOf<Participant>([
  userParticipantDecoder.map<Participant>(identity),
  groupParticipantDecoder.map<Participant>(identity),
]);

export const participantsResponseDecoder: Decoder<Participant[]> = field(
  'payload',
  succeed({})
    .assign('participants', field('participants', array(participantDecoder)))
    .map((response) => response.participants),
);

const learningCollectionDecoder: Decoder<LearningCollection> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('publishedAt', field('published_at', explicitMaybe(date)))
  .assign('license', field('license', explicitMaybe(licenseDecoder)))
  .assign('shareUrl', field('share_url', explicitMaybe(shareUrlResourceDecoder)))
  .assign('showUnlicensedContent', field('show_unlicensed_content', boolean))
  .assign('productLicenseId', field('product_license_id', explicitMaybe(number)))
  .assign('availableLicenses', field('available_licenses', readonlyArray(licenseOptionDecoder)))
  .assign('programs', field('programs', readonlyArray(programDecoder)));

export const learningCollectionResourceDecoder = resourceDecoder(learningCollectionDecoder);

const successfulParticipantDecoder: Decoder<SuccessfulParticipant> = succeed({})
  .assign('userId', field('user_id', number))
  .assign('email', field('email', string))
  .assign('name', field('name', string))
  .assign('status', field('status', stringLiteral('added')))
  .assign('invitationId', field('invitation_id', number));

const failedParticipantDecoder: Decoder<FailedParticipant> = succeed({})
  .assign('userId', maybe(field('user_id', number)))
  .assign('email', maybe(field('email', string)))
  .assign('error', field('error', string))
  .assign('status', field('status', stringLiteral('failed')));

export const shareLearningCollectionResponseDecoder: Decoder<ShareLearningCollectionResponse> =
  resourceDecoder(
    succeed({})
      .assign(
        'successfulParticipants',
        field('successful_participants', readonlyArray(successfulParticipantDecoder)),
      )
      .assign(
        'failedParticipants',
        field('failed_participants', readonlyArray(failedParticipantDecoder)),
      ),
  ).map((resource) => resource.payload);
